class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Use delegation so overlay click works even after drawer HTML is replaced (e.g. by cart-progress)
    this.addEventListener('click', (e) => {
      if (e.target.id === 'CartDrawer-Overlay' || e.target.classList.contains('cart-drawer__overlay')) {
        this.close();
      }
      // Recommendations carousel arrows (delegated so it works after dynamic inject)
      const arrow = e.target.closest('.cart-drawer-recommendations__arrow');
      if (arrow) {
        const scrollEl = this.querySelector('.cart-drawer-recommendations__scroll');
        if (scrollEl) {
          const step = 240;
          scrollEl.scrollBy({
            left: arrow.classList.contains('cart-drawer-recommendations__arrow--prev') ? -step : step,
            behavior: 'smooth'
          });
        }
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const sections = parsedState.sections;
    this.getSectionsToRender().forEach((section) => {
      if (!sections || sections[section.id] == null) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });

    setTimeout(() => this.open(), 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerDiscount extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const scriptEl = document.querySelector('script[data-cart-drawer-discount-strings]');
    if (scriptEl && scriptEl.textContent) {
      try {
        window.cartDrawerDiscountStrings = JSON.parse(scriptEl.textContent);
      } catch (e) {}
    }

    const promo = this.closest('.cart-drawer__promo') || document.getElementById('CartDrawer-Promo');
    if (promo) {
      const toggle = promo.querySelector('#CartDrawer-PromoToggle');
      const field = promo.querySelector('#CartDrawer-PromoField');
      if (toggle && field) {
        toggle.addEventListener('click', () => {
          const isExpanded = field.hidden;
          field.hidden = !isExpanded;
          toggle.setAttribute('aria-expanded', isExpanded);
          if (isExpanded && field.querySelector('input')) {
            field.querySelector('input').focus();
          }
        });
      }
    }

    const form = this.querySelector('#CartDrawer-DiscountForm');
    if (form && typeof fetch !== 'undefined' && window.routes && window.routes.cart_update_url) {
      form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const input = form.querySelector('#CartDrawer-DiscountInput');
    const applyBtn = form.querySelector('[name="apply"]');
    const messageEl = form.querySelector('#CartDrawer-DiscountMessage');
    const code = (input && input.value) ? input.value.trim() : '';
    if (!code) return;

    if (applyBtn) applyBtn.disabled = true;
    if (messageEl) {
      messageEl.hidden = true;
      messageEl.removeAttribute('data-status');
      messageEl.textContent = '';
    }

    const updateUrl = window.routes.cart_update_url.replace(/\/cart\/update$/, '/cart/update.js');
    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };

    fetch(updateUrl, { ...config, body: JSON.stringify({ discount: code }) })
      .then((res) => res.json().then((data) => ({ ok: res.ok, status: res.status, data })))
      .then(({ ok, status, data }) => {
        const isHttpError =
          !ok && (status === 422 || status === 400 || status === 404) ||
          (data && typeof data.status === 'number' && data.status >= 400) ||
          (data && data.message && data.description);
        const codeApplicable =
          Array.isArray(data.discount_codes) &&
          data.discount_codes.some(
            (dc) => dc && String(dc.code).toLowerCase() === code.toLowerCase() && dc.applicable === true
          );
        const isError = isHttpError || (data && data.discount_codes && !codeApplicable);
        if (isError) {
          if (messageEl && window.cartDrawerDiscountStrings && window.cartDrawerDiscountStrings.invalid) {
            messageEl.textContent = window.cartDrawerDiscountStrings.invalid;
            messageEl.setAttribute('data-status', 'error');
            messageEl.hidden = false;
          }
          return;
        }
        if (messageEl && window.cartDrawerDiscountStrings && window.cartDrawerDiscountStrings.success) {
          messageEl.textContent = window.cartDrawerDiscountStrings.success;
          messageEl.setAttribute('data-status', 'success');
          messageEl.hidden = false;
        }
        if (typeof publish !== 'undefined' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: data });
        }
        const drawerItems = document.querySelector('cart-drawer-items');
        if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
          drawerItems.onCartUpdate();
        }
      })
      .catch(() => {
        if (messageEl && window.cartDrawerDiscountStrings && window.cartDrawerDiscountStrings.invalid) {
          messageEl.textContent = window.cartDrawerDiscountStrings.invalid;
          messageEl.setAttribute('data-status', 'error');
          messageEl.hidden = false;
        }
      })
      .finally(() => {
        if (applyBtn) applyBtn.disabled = false;
      });
  }
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
